import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Clock, DollarSign, Utensils } from 'lucide-react';
export function HeroSection() {
  return <section className="relative overflow-hidden bg-cream pt-20 pb-20 lg:pt-28 lg:pb-28" data-id="element-22">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-golden/10 blur-3xl" data-id="element-23" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-terracotta/10 blur-3xl" data-id="element-24" />

      <div className="container mx-auto px-4 relative z-10" data-id="element-25">
        <div className="max-w-6xl mx-auto" data-id="element-26">
          {/* Two-column layout: text left, image right */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-27">
            {/* Text Column */}
            <div className="flex-1 text-center lg:text-left" data-id="element-28">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-29">
                <span className="inline-block py-1 px-3 rounded-full bg-terracotta/10 text-terracotta font-semibold text-sm mb-6 tracking-wide uppercase" data-id="element-30">
                  Para la mujer latina en USA
                </span>

                <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold text-espresso leading-tight mb-6 font-serif" data-id="element-31">
                  Dile adiós al{' '}
                  <span className="text-terracotta italic" data-id="element-32">"Drive-Thru"</span>{' '}
                  de las 6 PM y vuelve a comer como en casa.
                </h1>

                <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-33">
                  Cómo alimentar a tu familia con sabor latino en solo 10
                  minutos. (Incluso después de un turno de 10 horas).
                </p>

                <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10" data-id="element-34">
                  <Button className="w-full sm:w-auto text-xl px-10 py-5" onClick={() => {
                  const target = document.getElementById('cta-section');
                  if (!target) return;
                  const targetY = target.getBoundingClientRect().top + window.scrollY;
                  const startY = window.scrollY;
                  const distance = targetY - startY;
                  const duration = 1800;
                  let startTime: number | null = null;
                  const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
                  const step = (timestamp: number) => {
                    if (!startTime) startTime = timestamp;
                    const elapsed = timestamp - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    window.scrollTo(0, startY + distance * easeInOutCubic(progress));
                    if (progress < 1) requestAnimationFrame(step);
                  };
                  requestAnimationFrame(step);
                }} data-id="element-35">
                    Quiero mi Guía por $6,39
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-36" />
                  </Button>
                </div>

                {/* Social Proof / Benefits Pills */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-left max-w-xl mx-auto lg:mx-0" data-id="element-37">
                  <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl backdrop-blur-sm border border-stone-200" data-id="element-38">
                    <div className="bg-forest/10 p-2 rounded-full shrink-0" data-id="element-39">
                      <Clock className="h-4 w-4 text-forest" data-id="element-40" />
                    </div>
                    <span className="text-xs font-medium text-espresso" data-id="element-41">
                      10 min diarios
                    </span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl backdrop-blur-sm border border-stone-200" data-id="element-42">
                    <div className="bg-terracotta/10 p-2 rounded-full shrink-0" data-id="element-43">
                      <Utensils className="h-4 w-4 text-terracotta" data-id="element-44" />
                    </div>
                    <span className="text-xs font-medium text-espresso" data-id="element-45">
                      Sabor 100% Latino
                    </span>
                  </div>
                  <div className="flex items-center gap-3 bg-white/60 p-3 rounded-xl backdrop-blur-sm border border-stone-200" data-id="element-46">
                    <div className="bg-golden/10 p-2 rounded-full shrink-0" data-id="element-47">
                      <DollarSign className="h-4 w-4 text-golden" data-id="element-48" />
                    </div>
                    <span className="text-xs font-medium text-espresso" data-id="element-49">
                      Ahorra +$400/mes
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Image Column */}
            <motion.div initial={{
            opacity: 0,
            x: 30,
            rotate: 2
          }} animate={{
            opacity: 1,
            x: 0,
            rotate: 2
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="flex-shrink-0 w-full max-w-xs lg:max-w-sm" data-id="element-50">
              <div className="relative" data-id="element-51">
                {/* Decorative shadow/glow behind image */}
                <div className="absolute inset-0 bg-terracotta/20 rounded-2xl blur-2xl scale-95 translate-y-4" data-id="element-52" />
                <img src="https://cdn.magicpatterns.com/uploads/7tcyy1GSUxU8g1NJwWmNxP/MEAL_PREP_1X1.png" alt="Smart Meal Prep USA - Edición Latina" className="relative w-full rounded-2xl shadow-2xl" style={{
                transform: 'rotate(2deg)'
              }} data-id="element-53" />
                {/* Price badge */}
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.9,
                type: 'spring',
                stiffness: 200
              }} className="absolute -bottom-4 -left-4 bg-terracotta text-white rounded-full w-20 h-20 flex flex-col items-center justify-center shadow-xl font-bold" data-id="element-54">
                  <span className="text-xs leading-none" data-id="element-55">Solo</span>
                  <span className="text-lg leading-tight" data-id="element-56">$6,39</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}